import React from 'react';
import styled from 'styled-components';

import Badge, { KingpinBadgeType } from 'kingpin/atoms/Badge';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import InsightsAnswerBody from 'components/InsightsChat/InsightsAnswerBody';

import { RUN_TARGET_LABELS, productLabel } from './constants';
import { evalRunToMessage } from './evalRunToMessage';

// Routing first — it is the verdict — then the inputs the agent actually used,
// then the answer as a customer would see it. What changed since the previous
// run on this account is marked inline; that is what replaces a stored baseline.

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AnswerDiv = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 12px;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid var(--color-border-secondary);
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  border-bottom: 1px solid var(--color-border-primary);
  padding-bottom: 6px;
`;

const Input = ({
  label,
  items,
  added,
  dropped,
  testId,
}: {
  label: string;
  items: string[];
  added?: string[];
  dropped?: string[];
  testId: string;
}) => {
  if (items.length === 0 && !dropped?.length) {
    return null;
  }
  const isAdded = new Set(added ?? []);
  return (
    <InputRow data-testid={testId}>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        {label}
      </Typography.Body>
      <Chips>
        {items.map((item) => (
          <Badge
            key={item}
            badgeType={isAdded.has(item) ? 'Blue' : 'Default'}
            text={isAdded.has(item) ? `${item} · new` : item}
          />
        ))}
        {(dropped ?? []).map((item) => (
          <Badge key={`gone-${item}`} badgeType="Red" text={`${item} · gone`} />
        ))}
      </Chips>
    </InputRow>
  );
};

// The routing assertion's badge. Asking back on a clear question is its own
// neutral state, not a wrong turn.
const routingBadgeType = ({
  run,
  routingCheck,
}: {
  run: AgentEval.Run;
  routingCheck: AgentEval.CheckResult;
}): KingpinBadgeType => {
  if (routingCheck.advisory) {
    return 'Default';
  }
  if (routingCheck.pass) {
    return 'Green';
  }
  return run.verdict === 'clarified' ? 'Blue' : 'Red';
};

const Routing = ({ run }: { run: AgentEval.Run }) => {
  const { routing } = run;
  const routingCheck = run.checks.find((check) => check.name === 'routing');
  if (!routing || !routingCheck) {
    return null;
  }
  const expected = routing.candidates.map(productLabel);
  return (
    <>
      <InputRow data-testid="agent-eval-routing">
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          Routing
        </Typography.Body>
        <Chips>
          <Badge
            badgeType={routingBadgeType({ run, routingCheck })}
            text={routingCheck.detail ?? (routingCheck.pass ? 'Pass' : 'Fail')}
          />
        </Chips>
      </InputRow>
      <Input
        label={routing.mode === 'clarify' ? 'Should ask' : 'Should read'}
        items={
          routing.mode === 'clarify'
            ? [`which of ${expected.join(', ')}`]
            : expected
        }
        testId="agent-eval-routing-expected"
      />
      <Input
        label="Products read"
        items={routing.routedLabels}
        added={run.drift?.productsAdded}
        dropped={run.drift?.productsDropped}
        testId="agent-eval-routing-read"
      />
    </>
  );
};

const AssertionRow = ({
  label,
  checks,
  testId,
}: {
  label: string;
  checks: AgentEval.CheckResult[];
  testId: string;
}) => {
  if (checks.length === 0) {
    return null;
  }
  return (
    <InputRow data-testid={testId}>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        {label}
      </Typography.Body>
      <Chips>
        {checks.map((check) => {
          // Advisory results never decided the verdict, so a miss is a caution,
          // not a red.
          const failType = check.advisory ? 'Yellow' : 'Red';
          return (
            <Badge
              key={check.name}
              badgeType={check.pass ? 'Green' : failType}
              text={
                check.detail ? `${check.name}: ${check.detail}` : check.name
              }
            />
          );
        })}
      </Chips>
    </InputRow>
  );
};

const EvalRunResultView = ({ run }: { run: AgentEval.Run }) => {
  const decisiveChecks = run.checks.filter(
    (check) => check.name !== 'routing' && !check.advisory,
  );
  const advisoryChecks = run.checks.filter(
    (check) => check.name !== 'routing' && check.advisory,
  );
  const metricNames = run.resolvedMetrics.map(
    (metric) => metric.metricName ?? metric.metricId,
  );
  const fieldNames = run.fields.map((field) =>
    field.dataset ? `${field.dataset}.${field.field}` : field.field,
  );
  const usedNothing =
    metricNames.length === 0 &&
    run.datasets.length === 0 &&
    fieldNames.length === 0;
  // Drift is by metric ID; show the name where we can resolve it.
  const nameById = new Map(
    run.resolvedMetrics.map((metric) => [
      metric.metricId,
      metric.metricName ?? metric.metricId,
    ]),
  );
  const driftNames = (ids: string[] | undefined): string[] =>
    (ids ?? []).map((id) => nameById.get(id) ?? id);

  return (
    <Wrapper data-testid={`agent-eval-run-${run.id}`}>
      <div>
        <SectionHead>
          <Typography.Body type="Label">Inputs</Typography.Body>
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            {run.drift ? 'changed since the last run' : 'unchanged'}
          </Typography.Body>
        </SectionHead>
        <Routing run={run} />
        {usedNothing ? (
          <InputRow>
            <Typography.Body type="Body 12" color="var(--color-text-secondary)">
              Metrics
            </Typography.Body>
            <Typography.Body type="Body 12">
              No metric queries were run.
            </Typography.Body>
          </InputRow>
        ) : (
          <>
            <Input
              label="Metrics"
              items={metricNames}
              added={driftNames(run.drift?.metricsAdded)}
              dropped={driftNames(run.drift?.metricsDropped)}
              testId="agent-eval-used-metrics"
            />
            <Input
              label="Datasets"
              items={run.datasets}
              added={run.drift?.datasetsAdded}
              dropped={run.drift?.datasetsDropped}
              testId="agent-eval-used-datasets"
            />
            <Input
              label="Fields"
              items={fieldNames}
              testId="agent-eval-used-fields"
            />
          </>
        )}
        <AssertionRow
          label="Assertions"
          checks={decisiveChecks}
          testId="agent-eval-checks"
        />
        <AssertionRow
          label="Advisory"
          checks={advisoryChecks}
          testId="agent-eval-advisory-checks"
        />
      </div>

      <div>
        <SectionHead>
          <Typography.Body type="Label">Answer</Typography.Body>
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            {run.chartCount > 0
              ? `${run.chartCount} chart${run.chartCount === 1 ? '' : 's'}`
              : 'no chart'}
            {run.groundingGateFired ? ' · grounding gate fired' : ''}
          </Typography.Body>
        </SectionHead>
        {run.answer !== '' && (
          <AnswerDiv data-testid="agent-eval-answer">
            <InsightsAnswerBody message={evalRunToMessage(run)} />
          </AnswerDiv>
        )}
      </div>

      {run.suggestions.length > 0 && (
        <Chips data-testid="agent-eval-suggestions">
          <Typography.Body type="Label" color="var(--color-text-secondary)">
            {run.askedClarification ? 'Options offered' : 'Follow-ups offered'}
          </Typography.Body>
          {run.suggestions.map((suggestion) => (
            <Badge
              key={suggestion}
              badgeType={run.askedClarification ? 'Blue' : 'Default'}
              text={suggestion}
            />
          ))}
        </Chips>
      )}

      <Row centerAlign style={{ gap: 8 }}>
        <Typography.Body type="Body 12" color="var(--color-text-secondary)">
          {run.usedTools.length > 0
            ? `Tools: ${run.usedTools.join(', ')} · `
            : ''}
          {RUN_TARGET_LABELS[run.target]} · {run.kbVersion ?? run.kbSource} ·{' '}
          {(run.durationMs / 1000).toFixed(1)}s · ${run.costUsd.toFixed(3)}
        </Typography.Body>
      </Row>
    </Wrapper>
  );
};

export default EvalRunResultView;
